/**
 * Agendrix-inspired theme: calm, modern green primary with neutral grays and sharp semantic colors.
 * Soft teal/green navbar so the logo sits on a faint, official-feeling header, plus a subtle deep-green accent.
 */

// Primary – Agendrix-like zen green, but deeper and more saturated
export const PRIMARY_YELLOW = '#00A378' // primary deep green-teal
export const PRIMARY_YELLOW_DARK = '#00775A'
export const PRIMARY_YELLOW_LIGHT = '#27C495'
export const PRIMARY_ACCENT = '#FF9A7A' // richer peach accent

// Top bar – deeper mint/teal so the logo sits on a richer but still light header
// Slightly darker background, deeper bottom gradient, and a strong border for structure
export const NAVBAR_YELLOW = '#D4F5EB' // still light, but less washed out
export const NAVBAR_YELLOW_DARK = '#00A378' // deep green-teal at bottom of gradient
export const NAVBAR_BORDER = '#00624B' // darkest green-teal accent bottom line
export const NAVBAR_BORDER_SIZE = '2px' // bottom border width

// Grays and black (true black for brand)
export const BLACK = '#000000'
export const GRAY_900 = '#171717'
export const GRAY_700 = '#404040'
export const GRAY_500 = '#737373'
export const GRAY_300 = '#a3a3a3'
export const GRAY_200 = '#e5e5e5'
export const GRAY_100 = '#f5f5f5'
export const OFF_WHITE = '#fafafa'

// Interactive – darker gray for buttons, active tabs, menus (yellow reserved for navbar)
export const INTERACTIVE_GRAY = '#404040'
export const INTERACTIVE_GRAY_DARK = '#262626'
export const INTERACTIVE_GRAY_LIGHT = '#525252' // for dark theme buttons

// Semantic – sharp green/red/orange for clarity
export const SUCCESS_GREEN = PRIMARY_YELLOW
export const DANGER_RED = '#EF4444'
export const WARNING_AMBER = '#F97316'
export const INFO_BLUE = '#2563EB'

// ============================================
// THEME PALETTE
// ============================================
export const themeColors = {
  primary: PRIMARY_YELLOW,
  primaryDark: PRIMARY_YELLOW_DARK,
  primaryLight: PRIMARY_YELLOW_LIGHT,
  primaryAccent: PRIMARY_ACCENT,

  // Light theme – green navbar and primary actions; neutral gray surfaces
  light: {
    primary: PRIMARY_YELLOW,
    primaryDark: PRIMARY_YELLOW_DARK,
    secondary: GRAY_700,
    accent: PRIMARY_ACCENT,
    info: INFO_BLUE,
    backgroundPrimary: GRAY_100,
    backgroundSecondary: '#ffffff',
    backgroundTertiary: GRAY_200,
    backgroundCardPrimary: '#ffffff',
    backgroundCardSecondary: OFF_WHITE,
    backgroundElement: GRAY_200,
    backgroundBorder: GRAY_200,
    textPrimary: BLACK,
    textSecondary: GRAY_700,
    textTertiary: GRAY_500,
    textInverted: '#ffffff',
    successColor: SUCCESS_GREEN,
    dangerColor: DANGER_RED,
    warningColor: WARNING_AMBER,
    statusActiveBg: 'rgba(64, 64, 64, 0.12)',
    statusWarningBg: '#FEF3C7',
    statusErrorBg: '#FEE2E2',
    statusSuccessBg: 'rgba(5, 150, 105, 0.12)',
    borderColor: GRAY_200,
    cardBorder: GRAY_200,
    dividerColor: 'rgba(0,0,0,0.06)',
    headerBg: '#ffffff',
    navbarBg: NAVBAR_YELLOW,
    navbarBgBottom: NAVBAR_YELLOW_DARK,
    navbarBorderColor: NAVBAR_BORDER,
    navbarBorderWidth: NAVBAR_BORDER_SIZE,
    navbarTextColor: BLACK,
    tabBg: '#ffffff',
    tabActiveBg: 'rgba(64, 64, 64, 0.12)',
    themeSurface: '#ffffff',
    themeSurfaceVariant: GRAY_200,
    themeToggleBg: GRAY_200,
  },

  // Dark theme – black/gray base; green navbar and primary accents
  dark: {
    primary: PRIMARY_YELLOW_LIGHT,
    primaryDark: PRIMARY_YELLOW_DARK,
    secondary: GRAY_300,
    accent: PRIMARY_ACCENT,
    info: '#60A5FA',
    backgroundPrimary: BLACK,
    backgroundSecondary: GRAY_900,
    backgroundTertiary: '#262626',
    backgroundCardPrimary: GRAY_900,
    backgroundCardSecondary: BLACK,
    backgroundElement: '#262626',
    backgroundBorder: '#262626',
    textPrimary: '#fafafa',
    textSecondary: GRAY_300,
    textTertiary: GRAY_500,
    textInverted: BLACK,
    successColor: '#34D399',
    dangerColor: '#F87171',
    warningColor: '#FBBF24',
    statusActiveBg: 'rgba(82, 82, 82, 0.35)',
    statusWarningBg: 'rgba(251, 191, 36, 0.1)',
    statusErrorBg: 'rgba(248, 113, 113, 0.15)',
    statusSuccessBg: 'rgba(52, 211, 153, 0.12)',
    borderColor: '#262626',
    cardBorder: '#262626',
    dividerColor: 'rgba(255,255,255,0.06)',
    headerBg: BLACK,
    navbarBg: NAVBAR_YELLOW,
    navbarBgBottom: NAVBAR_YELLOW_DARK,
    navbarBorderColor: NAVBAR_BORDER,
    navbarBorderWidth: NAVBAR_BORDER_SIZE,
    navbarTextColor: BLACK,
    tabBg: GRAY_900,
    tabActiveBg: 'rgba(82, 82, 82, 0.35)',
    themeSurface: GRAY_900,
    themeSurfaceVariant: '#262626',
    themeToggleBg: '#262626',
  },

  shadows: {
    light: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.07), 0 2px 4px -2px rgba(0, 0, 0, 0.04)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.04)',
      inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.03)',
    },
    dark: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.4)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.45), 0 2px 4px -2px rgba(0, 0, 0, 0.35)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -4px rgba(0, 0, 0, 0.4)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.55), 0 8px 10px -6px rgba(0, 0, 0, 0.45)',
      inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.4)',
    },
  },
} as const

export function getThemeColors(themeType: 'light' | 'dark') {
  return themeType === 'light' ? themeColors.light : themeColors.dark
}

export function getThemeShadows(themeType: 'light' | 'dark') {
  return themeType === 'light' ? themeColors.shadows.light : themeColors.shadows.dark
}

export function getStatusBgColor(themeType: 'light' | 'dark', status: 'active' | 'warning' | 'error' | 'success') {
  const colors = getThemeColors(themeType)
  switch (status) {
    case 'active':
      return colors.statusActiveBg
    case 'warning':
      return colors.statusWarningBg
    case 'error':
      return colors.statusErrorBg
    case 'success':
      return colors.statusSuccessBg || colors.statusActiveBg
    default:
      return colors.statusActiveBg
  }
}
